import { useState, useEffect, useCallback } from "react";

const MOBILE_BREAKPOINT = 768;
const TABLET_BREAKPOINT = 1024;

export function useIsMobile() {
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    // Initial check based on screen width
    if (typeof window !== "undefined") {
      return window.innerWidth < MOBILE_BREAKPOINT;
    }
    return false;
  });

  // Use useCallback to memoize the resize handler
  const handleResize = useCallback(() => {
    setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
  }, []);

  useEffect(() => {
    // Create debounced resize handler for better performance
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const debouncedResize = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      timeoutId = setTimeout(handleResize, 100);
    };

    window.addEventListener("resize", debouncedResize);

    // Make sure the initial value is correct after mount
    handleResize();

    return () => {
      window.removeEventListener("resize", debouncedResize);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [handleResize]);

  return isMobile;
}

export const useMediaQuery = (query: string): boolean => {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const media = window.matchMedia(query);
    if (media.matches !== matches) {
      setMatches(media.matches);
    }
    const listener = () => setMatches(media.matches);
    window.addEventListener("resize", listener);
    return () => window.removeEventListener("resize", listener);
  }, [matches, query]);

  return matches;
};

export const useIsExtraSmall = (): boolean => {
  return useMediaQuery("(max-width: 480px)");
};

export function useIsTablet() {
  const [isTablet, setIsTablet] = useState<boolean>(() => {
    // Initial check based on screen width
    if (typeof window !== "undefined") {
      return (
        window.innerWidth >= MOBILE_BREAKPOINT &&
        window.innerWidth < TABLET_BREAKPOINT
      );
    }
    return false;
  });

  // Use useCallback to memoize the resize handler
  const handleResize = useCallback(() => {
    setIsTablet(
      window.innerWidth >= MOBILE_BREAKPOINT &&
        window.innerWidth < TABLET_BREAKPOINT
    );
  }, []);

  useEffect(() => {
    // Create debounced resize handler for better performance
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const debouncedResize = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      timeoutId = setTimeout(handleResize, 100);
    };

    window.addEventListener("resize", debouncedResize);

    // Make sure the initial value is correct after mount
    handleResize();

    return () => {
      window.removeEventListener("resize", debouncedResize);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [handleResize]);

  return isTablet;
}
